import { KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import {
    BaseSandbox,
    fragmentsToFormSelectOptions,
    RecordsService,
    RelationItem,
    RelationsOfType,
    RelationsService,
    RelationType
} from '@mediahaven/shared';
import {
    ApiFragment,
    ApiQuery,
    BaseNestedFieldDefinition,
    Combine,
    DottedKey,
    Family,
    FormSelectOption,
    FragmentId,
    isNotNil,
    MediaObjectId,
    MergeStrategy,
    MultiFormSelectOptions,
    multiFormSelectOptionWrapper,
    PaginationInfo,
    PartialRecord,
    QueryPart,
    QueryPartStrategy,
    RecordId,
    RecordPhase,
    RecordRestrictions,
    RecordStatus,
    RecordType,
    Restriction,
    ShFieldDefinitionService,
    SortDirection,
    SortInfo,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';

@Injectable({ providedIn: 'root' })
export class FragmentsSandbox extends BaseSandbox {
    constructor(
        private readonly fieldDefinitionService: ShFieldDefinitionService,
        private readonly recordsService: RecordsService,
        private readonly relationsService: RelationsService,
        private readonly zoneRestrictions: ZoneRestrictions,
        private readonly zoneService: ZonesService
    ) {
        super();
    }

    public getRecord(fragmentId: FragmentId): Observable<ApiFragment> {
        return this.recordsService.getRecord(fragmentId);
    }

    public getFragmentCollection(fragment: ApiFragment): Observable<ApiFragment | undefined> {
        const collectionId: MediaObjectId | null = fragment.getCollectionReferenceCode();
        const candidateCollectionId: FragmentId | null = fragment.getCandidateParentId();

        if (collectionId) {
            return this.recordsService.getRecord(collectionId);
        }

        if (candidateCollectionId) {
            return this.recordsService.getRecord(candidateCollectionId);
        }

        return of(undefined);
    }

    public getFragmentNonSystemRelations(fragment: ApiFragment): Observable<RelationsOfType[]> {
        return this.relationsService.getRelationTypes().pipe(
            map(relationTypes => relationTypes.filter(relationType => !relationType.IsSystem)),
            take(1),
            switchMap(relationTypes =>
                this.relationsService
                    .getRecordRelations(fragment, relationTypes)
                    .pipe(map(relationFragments => ({ relationTypes, relationFragments })))
            ),
            map(({ relationTypes, relationFragments }) =>
                this.groupFragmentsByRelationType(fragment, relationFragments, relationTypes)
            )
        );
    }

    private groupFragmentsByRelationType(
        fragment: ApiFragment,
        relationFragments: ApiFragment[],
        relationTypes: RelationType[]
    ): RelationsOfType[] {
        return relationTypes.map(relationType => {
            const type: string = relationType.RelationType;
            const allRelationsOfType: FragmentId[] = fragment.getRelationFragmentIdsOfType(relationType.RelationType);

            const relationsOfType: ApiFragment[] = relationFragments.filter(relation =>
                allRelationsOfType.includes(relation.getFragmentId())
            );

            const relations: RelationItem[] = relationsOfType.map(
                relationFragment => new RelationItem({ fragment: relationFragment, relationType: type })
            );

            return {
                relationType,
                relations
            };
        });
    }

    private getEditableType(
        searchTerm: string,
        existingRestriction: Restriction = Combine.Noop(),
        recordType?: RecordType
    ): Observable<ApiFragment[]> {
        return this.getCurrentUser().pipe(
            withLatestFrom(this.zoneService.zones$),
            take(1),
            switchMap(([user, zones]) => {
                const searchQuery: QueryPart[] = [
                    recordType
                        ? new QueryPart({
                              field: 'RecordType',
                              strategy: QueryPartStrategy.MatchAll,
                              values: [recordType]
                          })
                        : null,
                    new QueryPart({
                        field: 'q',
                        strategy: QueryPartStrategy.MatchAll,
                        values: [`*${searchTerm}*`]
                    })
                ].filter(isNotNil);

                const restriction: Restriction = Combine.And(
                    existingRestriction,
                    this.zoneRestrictions.regularRecordTypesZone(),
                    Combine.Or(
                        Combine.And(
                            RecordRestrictions.phaseRestriction([RecordPhase.Published]),
                            RecordRestrictions.editableRestriction(user.Groups)
                        ),
                        RecordRestrictions.conceptZoneRestriction(zones)
                    )
                );

                const query: ApiQuery = new ApiQuery(searchQuery, restriction);

                return this.simpleSearchWrapper(query);
            })
        );
    }

    private getEditableRecords(
        searchTerm: string,
        excludeFragmentId?: FragmentId,
        withRestrictions?: Restriction[]
    ): Observable<ApiFragment[]> {
        return this.userStoreService.getUserFunctions().pipe(
            switchMap(userFunctions => {
                const restrictions: Restriction[] = withRestrictions ?? [];

                if (!userFunctions.EVALUATE_MEDIA) {
                    restrictions.push(RecordRestrictions.excludeSubmittedRestriction());
                }

                if (excludeFragmentId) {
                    restrictions.push(RecordRestrictions.excludeFragmentByIdRestriction(excludeFragmentId));
                }
                const restriction: Restriction = Combine.And(...restrictions);

                return this.getEditableType(searchTerm, restriction);
            }),
            map(records => records.filter(record => record.canBeEdited()))
        );
    }

    private getEditableCollections(searchTerm: string, excludeFragmentId?: FragmentId): Observable<ApiFragment[]> {
        let restriction: Restriction | undefined;

        if (excludeFragmentId) {
            restriction = RecordRestrictions.excludeFragmentByIdRestriction(excludeFragmentId);
        }

        return this.getEditableType(searchTerm, restriction, RecordType.Mh2Collection);
    }

    private simpleSearchWrapper(query: ApiQuery): Observable<ApiFragment[]> {
        const paginationInfo: PaginationInfo = {
            resultsPerPage: 25,
            page: 0,
            totalNumberOfResults: 0
        };

        const sortInfo: SortInfo = { field: 'Title', direction: SortDirection.ASC };
        return this.recordsService.getRecords(paginationInfo, sortInfo, query).pipe(map(results => results.results));
    }

    public deleteRelation(fragment: ApiFragment, relationItem: RelationItem): Observable<ApiFragment> {
        const fragmentRelations: FragmentId[] = fragment.getRelationFragmentIdsOfType(relationItem.relationType);

        const newFragmentRelations: FragmentId[] = fragmentRelations.filter(
            fragmentRelation => fragmentRelation !== relationItem.fragment.getFragmentId()
        );

        const metadata: PartialRecord = new PartialRecord();
        metadata.set(Family.Structural, 'Relations', {
            ...fragment.get(Family.Structural, 'Relations'),
            [relationItem.relationType]: newFragmentRelations
        });
        metadata.addMergeStrategy('Relations', MergeStrategy.OVERWRITE);

        return this.recordsService.saveRecord(fragment.getFragmentId(), [metadata]);
    }

    public getNonSystemRelationTypesOptions(): Observable<MultiFormSelectOptions> {
        return this.getNonSystemRelationTranslations().pipe(
            map(values => {
                const options: FormSelectOption[] = values.map(({ key, value }) => ({ label: value, value: key }));
                return multiFormSelectOptionWrapper(options);
            })
        );
    }

    public getNonSystemRelationTranslations(): Observable<KeyValue<string, string>[]> {
        return combineLatest([
            this.getChildKeyAndTranslation('Structural.Relations' as DottedKey),
            this.relationsService.getRelationTypes()
        ]).pipe(
            map(([translations, types]) => {
                const nonSystemTypes: string[] = types
                    .filter(relationType => !relationType.IsSystem)
                    .map(relationType => relationType.RelationType);

                return translations.filter(({ key, value }) => nonSystemTypes.includes(key));
            }),
            take(1)
        );
    }

    private getChildKeyAndTranslation(dottedKey: DottedKey): Observable<KeyValue<string, string>[]> {
        return this.fieldDefinitionService.getByDottedKey(dottedKey).pipe(
            map(fragment => fragment?.Children ?? []),
            map(children => children.filter((child): child is BaseNestedFieldDefinition => !!child)),
            map(children => children.map(child => ({ key: child.Key, value: child.Label })))
        );
    }

    public getFragmentsAsFormSelectOptions(
        searchTerm: string,
        excludeFragment?: FragmentId
    ): Observable<MultiFormSelectOptions<ApiFragment>> {
        return this.getEditableRecords(searchTerm, excludeFragment).pipe(
            map((fragments: ApiFragment[]) => fragmentsToFormSelectOptions(fragments))
        );
    }

    public getCollectionsAsFormSelectOptions(
        searchTerm: string,
        excludeCollectionId?: FragmentId
    ): Observable<MultiFormSelectOptions<ApiFragment>> {
        return this.getEditableCollections(searchTerm, excludeCollectionId).pipe(
            map((collections: ApiFragment[]) => fragmentsToFormSelectOptions(collections))
        );
    }

    public getRecordsOfTypeAsFormSelectOptions(
        searchTerm: string,
        recordType: RecordType,
        recordsToOmit: RecordId[]
    ): Observable<MultiFormSelectOptions<ApiFragment>> {
        const searchQuery: QueryPart[] = [
            new QueryPart({
                field: 'RecordType',
                strategy: QueryPartStrategy.MatchAll,
                values: [recordType]
            })
        ];

        if (recordsToOmit.length > 0) {
            searchQuery.push(
                new QueryPart({
                    field: 'RecordId',
                    strategy: QueryPartStrategy.MatchNone,
                    values: recordsToOmit
                })
            );
        }

        if (searchTerm) {
            searchQuery.push(
                new QueryPart({
                    field: 'Title',
                    strategy: QueryPartStrategy.MatchAll,
                    values: [`*${searchTerm}*`]
                })
            );
        }

        return this.getCurrentUser().pipe(
            withLatestFrom(this.zoneService.zones$),
            take(1),
            switchMap(([user, zones]) => {
                const restriction: Restriction = this.zoneRestrictions.allParentRecordsInZone(zones, user.Groups);

                const query: ApiQuery = new ApiQuery(searchQuery, restriction);

                return this.simpleSearchWrapper(query).pipe(
                    map((collections: ApiFragment[]) => fragmentsToFormSelectOptions(collections)),
                    map(formOptions => ({
                        isLiveData: formOptions.isLiveData,
                        options: formOptions.options.map(option => ({
                            ...option,
                            disabled: option.value.getRecordStatus() !== RecordStatus.Submitted
                        }))
                    }))
                );
            })
        );
    }
}
